import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, Users, Search, Crown, Wrench, UserCog, Bot, UserPlus, Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { apiRequest, queryClient } from "@/lib/queryClient";

type OrgUser = {
  id: string;
  email: string;
  name: string;
  role: string;
  department: string | null;
  jobTitle: string | null;
  active: string;
};

// Roles a tenant admin is allowed to assign. Order matters — this is
// also the column order in the toggle grid. "super_admin" is platform
// only and is intentionally absent.
const ROLE_DEFS: { value: string; label: string; help: string; icon: any; tone: string }[] = [
  { value: "org_admin", label: "Org Admin",  help: "Full tenant admin access",                 icon: Shield,  tone: "text-amber-500" },
  { value: "it_admin",  label: "IT Admin",   help: "Manage SSO, integrations & identity",      icon: Wrench,  tone: "text-sky-500" },
  { value: "hr_admin",  label: "HR Admin",   help: "Manage workforce & user roles",            icon: UserCog, tone: "text-emerald-500" },
  { value: "bot_admin", label: "Bot Admin",  help: "Create, edit & assign chatbots",           icon: Bot,     tone: "text-purple-500" },
  { value: "manager",   label: "Manager",    help: "Lead a team — no admin powers",            icon: Users,   tone: "text-indigo-400" },
  { value: "employee",  label: "Employee",   help: "Standard end-user access",                 icon: Users,   tone: "text-slate-400" },
];

const ADMIN_ROLE_VALUES = new Set(["org_admin", "it_admin", "hr_admin", "bot_admin", "admin"]);

// Identity-admin roles allowed to *create* users (matches server-side
// requireIdentityAdmin gate). bot_admin / hr_admin can change other
// fields but not invite new accounts.
const CAN_CREATE_USERS = new Set(["admin", "super_admin", "org_admin", "it_admin"]);

type NewUserDraft = {
  email: string;
  name: string;
  role: string;
  department: string;
  jobTitle: string;
};

const EMPTY_DRAFT: NewUserDraft = {
  email: "",
  name: "",
  role: "employee",
  department: "",
  jobTitle: "",
};

export default function OrgRolesPage() {
  const { user: me } = useAuth();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState<NewUserDraft>(EMPTY_DRAFT);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [createdEmail, setCreatedEmail] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const canCreate = CAN_CREATE_USERS.has(me?.role || "");

  const { data: users, isLoading } = useQuery<OrgUser[]>({ queryKey: ["/api/org/users"] });

  const updateRole = useMutation({
    mutationFn: async ({ id, role }: { id: string; role: string }) => {
      const res = await apiRequest("PATCH", `/api/org/users/${id}`, { role });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/org/users"] });
      toast({ title: "Role updated", description: `${data.email} is now ${data.role.replace(/_/g, " ")}.` });
    },
    onError: (err: Error) => {
      toast({ title: "Couldn't update role", description: err.message, variant: "destructive" });
    },
  });

  const updateActive = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const res = await apiRequest("PATCH", `/api/org/users/${id}`, { active });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/org/users"] });
      toast({ title: data.active === "true" ? "User activated" : "User deactivated", description: data.email });
    },
    onError: (err: Error) => {
      toast({ title: "Couldn't update user", description: err.message, variant: "destructive" });
    },
  });

  const createUser = useMutation({
    mutationFn: async (payload: NewUserDraft) => {
      const res = await apiRequest("POST", "/api/org/users", payload);
      return res.json() as Promise<{ user: OrgUser; tempPassword: string }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/org/users"] });
      // Switch the dialog into "show temp password" mode so the admin
      // can hand the credentials over before the dialog is dismissed.
      setTempPassword(data.tempPassword);
      setCreatedEmail(data.user.email);
      setCopied(false);
    },
    onError: (err: Error) => {
      toast({ title: "Couldn't add user", description: err.message, variant: "destructive" });
    },
  });

  const closeAddDialog = () => {
    setAddOpen(false);
    // Reset only after the close animation finishes so the form doesn't
    // visibly empty itself mid-transition.
    setTimeout(() => {
      setDraft(EMPTY_DRAFT);
      setTempPassword(null);
      setCreatedEmail(null);
      setCopied(false);
    }, 150);
  };

  const copyTempPassword = async () => {
    if (!tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast({ title: "Copy failed", description: "Select the password manually to copy.", variant: "destructive" });
    }
  };

  const submitNewUser = (e: React.FormEvent) => {
    e.preventDefault();
    createUser.mutate(draft);
  };

  const filtered = useMemo(() => {
    if (!users) return [];
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      u.email.toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q) ||
      (u.role || "").toLowerCase().includes(q) ||
      (u.department || "").toLowerCase().includes(q),
    );
  }, [users, search]);

  const adminCount = useMemo(
    () => (users || []).filter(u => ADMIN_ROLE_VALUES.has(u.role) && u.active === "true").length,
    [users],
  );

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">Role Management</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Toggle which roles each teammate has. Each user has exactly one role at a time.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Badge variant="outline" data-testid="badge-active-admins">
            <Crown className="w-3 h-3 mr-1" />
            {adminCount} active admin{adminCount === 1 ? "" : "s"}
          </Badge>
          <Badge variant="outline" data-testid="badge-total-users">
            <Users className="w-3 h-3 mr-1" />
            {users?.length || 0} user{users?.length === 1 ? "" : "s"}
          </Badge>
          {canCreate && (
            <Button
              size="sm"
              onClick={() => setAddOpen(true)}
              data-testid="button-add-user"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          )}
        </div>
      </div>

      {/* Add User dialog. Two visual states:
          1) Form — capture email/name/role/dept/title
          2) Success — show one-time temp password the admin must hand off */}
      <Dialog open={addOpen} onOpenChange={(open) => (open ? setAddOpen(true) : closeAddDialog())}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-add-user">
          {tempPassword ? (
            <>
              <DialogHeader>
                <DialogTitle>User created</DialogTitle>
                <DialogDescription>
                  Share these credentials with <span className="font-medium text-foreground">{createdEmail}</span>. This password is shown only once — copy it now.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <div className="mt-1 px-3 py-2 rounded-md bg-muted font-mono text-sm" data-testid="text-created-email">
                    {createdEmail}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Temporary password</Label>
                  <div className="mt-1 flex gap-2">
                    <div className="flex-1 px-3 py-2 rounded-md bg-muted font-mono text-sm break-all" data-testid="text-temp-password">
                      {tempPassword}
                    </div>
                    <IconButton
                      variant="outline"
                      label="Copy temporary password"
                      onClick={copyTempPassword}
                      data-testid="button-copy-password"
                    >
                      {copied ? <Check className="w-4 h-4 text-emerald-500" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
                    </IconButton>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  If your org uses SSO, ignore the password — the user will sign in through your identity provider.
                </p>
              </div>
              <DialogFooter>
                <Button onClick={closeAddDialog} data-testid="button-done">Done</Button>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={submitNewUser}>
              <DialogHeader>
                <DialogTitle>Add a new user</DialogTitle>
                <DialogDescription>
                  Create an account in your organization. They'll receive a temporary password you can hand off, or sign in via SSO if configured.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-1.5">
                  <Label htmlFor="new-user-email">Email <span className="text-destructive">*</span></Label>
                  <Input
                    id="new-user-email"
                    type="email"
                    required
                    autoComplete="off"
                    value={draft.email}
                    onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                    placeholder="alex@oakland.edu"
                    data-testid="input-new-email"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="new-user-name">Name <span className="text-destructive">*</span></Label>
                  <Input
                    id="new-user-name"
                    required
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="Alex Johnson"
                    data-testid="input-new-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="new-user-role">Role</Label>
                  <Select value={draft.role} onValueChange={(v) => setDraft({ ...draft, role: v })}>
                    <SelectTrigger id="new-user-role" data-testid="select-new-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_DEFS.map((r) => (
                        <SelectItem key={r.value} value={r.value} data-testid={`option-role-${r.value}`}>
                          <div className="flex items-center gap-2">
                            <r.icon className={`w-3.5 h-3.5 ${r.tone}`} />
                            <span>{r.label}</span>
                            <span className="text-xs text-muted-foreground">— {r.help}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="new-user-dept">Department</Label>
                    <Input
                      id="new-user-dept"
                      value={draft.department}
                      onChange={(e) => setDraft({ ...draft, department: e.target.value })}
                      placeholder="UTS"
                      data-testid="input-new-department"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="new-user-title">Job title</Label>
                    <Input
                      id="new-user-title"
                      value={draft.jobTitle}
                      onChange={(e) => setDraft({ ...draft, jobTitle: e.target.value })}
                      placeholder="Help Desk Analyst"
                      data-testid="input-new-jobtitle"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeAddDialog} data-testid="button-cancel-add">
                  Cancel
                </Button>
                <Button type="submit" disabled={createUser.isPending} data-testid="button-submit-add">
                  {createUser.isPending ? "Creating…" : "Create user"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>Team Members</CardTitle>
              <CardDescription>Flip a switch to grant a role; toggling on a different role replaces the current one.</CardDescription>
            </div>
            <div className="relative w-72">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, role…"
                className="pl-8"
                data-testid="input-search-users"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th scope="col" className="text-left font-medium px-4 py-3 sticky left-0 bg-muted/40 z-10">User</th>
                  {ROLE_DEFS.map(r => (
                    <th scope="col" key={r.value} className="text-center font-medium px-3 py-3" title={r.help}>
                      <div className="flex flex-col items-center gap-1">
                        <r.icon className={`w-3.5 h-3.5 ${r.tone}`} />
                        <span className="text-[10px] leading-tight">{r.label}</span>
                      </div>
                    </th>
                  ))}
                  <th scope="col" className="text-center font-medium px-4 py-3 border-l">Active</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={ROLE_DEFS.length + 2} className="text-center text-muted-foreground py-12" data-testid="text-no-users">
                      No users match your search.
                    </td>
                  </tr>
                )}
                {filtered.map((u, idx) => {
                  const isMe = u.id === me?.id;
                  const isPending = updateRole.isPending && updateRole.variables?.id === u.id;
                  const isActive = u.active === "true";
                  return (
                    <tr
                      key={u.id}
                      className={`border-t hover:bg-muted/20 ${idx % 2 === 1 ? "bg-muted/5" : ""}`}
                      data-testid={`row-user-${u.id}`}
                    >
                      <td className="px-4 py-3 sticky left-0 bg-background z-10">
                        <div className="min-w-[200px]">
                          <div className="flex items-center gap-2">
                            <p className="font-medium" data-testid={`text-name-${u.id}`}>{u.name || u.email}</p>
                            {isMe && <Badge variant="secondary" className="text-[9px]">You</Badge>}
                            {!isActive && <Badge variant="outline" className="text-[9px] text-muted-foreground">Inactive</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground truncate" data-testid={`text-email-${u.id}`}>{u.email}</p>
                          {(u.department || u.jobTitle) && (
                            <p className="text-[11px] text-muted-foreground/70 truncate">
                              {[u.jobTitle, u.department].filter(Boolean).join(" · ")}
                            </p>
                          )}
                        </div>
                      </td>
                      {ROLE_DEFS.map(r => {
                        const checked = u.role === r.value;
                        return (
                          <td key={r.value} className="text-center px-3 py-3">
                            <div className="flex justify-center">
                              <Switch
                                checked={checked}
                                disabled={isPending || (checked && isMe && ADMIN_ROLE_VALUES.has(r.value))}
                                onCheckedChange={(next) => {
                                  if (!next) return; // toggling off a role does nothing — pick a different one instead
                                  if (checked) return;
                                  updateRole.mutate({ id: u.id, role: r.value });
                                }}
                                aria-label={`Set ${u.name || u.email} to ${r.label}`}
                                data-testid={`switch-${u.id}-${r.value}`}
                              />
                            </div>
                          </td>
                        );
                      })}
                      <td className="text-center px-4 py-3 border-l">
                        <div className="flex justify-center">
                          <Switch
                            checked={isActive}
                            disabled={updateActive.isPending && updateActive.variables?.id === u.id}
                            onCheckedChange={(next) => updateActive.mutate({ id: u.id, active: next })}
                            aria-label={`${isActive ? "Deactivate" : "Activate"} ${u.name || u.email}`}
                            data-testid={`switch-active-${u.id}`}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Role Reference</CardTitle>
          <CardDescription>What each role can do in your organization.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {ROLE_DEFS.map(r => (
              <div key={r.value} className="flex items-start gap-3 p-3 rounded-lg border bg-card" data-testid={`legend-${r.value}`}>
                <r.icon className={`w-4 h-4 mt-0.5 ${r.tone}`} />
                <div>
                  <p className="text-sm font-medium">{r.label}</p>
                  <p className="text-xs text-muted-foreground">{r.help}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
